import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { useAuth } from '@/hooks/useAuth'

// Pages
import LoginPage from '@/features/auth/LoginPage'
import DashboardPage from '@/features/dashboard/DashboardPage'
import OperationsPage from '@/features/operations/OperationsPage'
import CriticalNodesPage from '@/features/nodes/CriticalNodesPage'
import ReportsPage from '@/features/reports/ReportsPage'
import MapPage from '@/features/map/MapPage'
import WelcomePage from '@/features/general/WelcomePage'
import DownloadPage from '@/features/general/DownloadPage'
import EvaluationPage from '@/features/evaluation/EvaluationPage'
import ManualsPage from '@/features/support/ManualsPage'
import LiveStreamPage from '@/features/streaming/LiveStreamPage'
import SignalChatPage from '@/features/signal/SignalChatPage'

// Billing
import BillingPage from '@/features/billing/BillingPage'

// Admin Pages
import UsersPage from '@/features/admin/UsersPage'
import AgenciesPage from '@/features/admin/AgenciesPage'
import AlertsPage from '@/features/admin/AlertsPage'
import DeviceManagementPage from '@/features/admin/DeviceManagementPage'

import AdminToolsPage from '@/features/admin/AdminToolsPage'

// Field
import EnrollmentPage from '@/features/field/EnrollmentPage'

// Legal
import LegalPage from '@/features/legal/LegalPage'
import CookieConsentBanner from '@/components/legal/CookieConsentBanner'
import TermsGate from '@/components/legal/TermsGate'

// Operaciones (nuevos módulos)
import GeofencesPage from '@/features/geofences/GeofencesPage'
import DispatchPage from '@/features/dispatch/DispatchPage'
import EmergenciesPage from '@/features/emergency/EmergenciesPage'
import CredentialsPage from '@/features/credentials/CredentialsPage'
import PatrolPage from '@/features/patrol/PatrolPage'
import SchedulingPage from '@/features/scheduling/SchedulingPage'
import SafetyPage from '@/features/safety/SafetyPage'
import EvidencePage from '@/features/evidence/EvidencePage'
import NotificationsPage from '@/features/notifications/NotificationsPage'

// Components
import ProtectedRoute from '@/features/auth/ProtectedRoute'
import Layout from '@/components/layout/Layout'

// Plataforma (admin de sistema)
import PlatformProtectedRoute from '@/features/platform/PlatformProtectedRoute'
import PlatformLoginPage from '@/features/platform/PlatformLoginPage'
import PlatformLayout from '@/features/platform/PlatformLayout'
import PlatformDashboardPage from '@/features/platform/PlatformDashboardPage'
import TenantsManagementPage from '@/features/platform/TenantsManagementPage'
import PlatformSecurityPage from '@/features/platform/PlatformSecurityPage'
import PlatformHealthPage from '@/features/platform/PlatformHealthPage'
import PlatformAuditPage from '@/features/platform/PlatformAuditPage'
import PlatformBillingPage from '@/features/platform/PlatformBillingPage'
import LeadsPage from '@/features/platform/LeadsPage'
import AccountsPage from '@/features/platform/AccountsPage'
import PipelinePage from '@/features/platform/PipelinePage'
import BiPage from '@/features/platform/BiPage'
import PlatformMaintenancePage from '@/features/platform/PlatformMaintenancePage'
import PlatformPlansPage from '@/features/platform/PlatformPlansPage'

function App() {
    const { initAuth } = useAuth()

    useEffect(() => {
        initAuth()
    }, [initAuth])

    return (
        <>
            <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/descargar" element={<DownloadPage />} />

                {/* Legal (públicas) */}
                <Route path="/terms" element={<LegalPage type="TERMS" />} />
                <Route path="/privacy" element={<LegalPage type="PRIVACY" />} />
                <Route path="/ai-disclosure" element={<LegalPage type="AI_DISCLOSURE" />} />

                {/* Plataforma — admin de sistema (auth y layout propios) */}
                <Route path="/platform/login" element={<PlatformLoginPage />} />
                <Route element={<PlatformProtectedRoute />}>
                    <Route element={<PlatformLayout />}>
                        <Route path="/platform/dashboard" element={<PlatformDashboardPage />} />
                        <Route path="/platform/tenants" element={<TenantsManagementPage />} />
                        <Route path="/platform/security" element={<PlatformSecurityPage />} />
                        <Route path="/platform/health" element={<PlatformHealthPage />} />
                        <Route path="/platform/audit" element={<PlatformAuditPage />} />
                        <Route path="/platform/billing" element={<PlatformBillingPage />} />
                        <Route path="/platform/leads" element={<LeadsPage />} />
                        <Route path="/platform/accounts" element={<AccountsPage />} />
                        <Route path="/platform/pipeline" element={<PipelinePage />} />
                        <Route path="/platform/bi" element={<BiPage />} />
                        <Route path="/platform/maintenance" element={<PlatformMaintenancePage />} />
                        <Route path="/platform/plans" element={<PlatformPlansPage />} />
                        <Route path="/platform" element={<Navigate to="/platform/dashboard" replace />} />
                    </Route>
                </Route>

                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                    <Route element={<Layout />}>
                        <Route path="/welcome" element={<WelcomePage />} />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/operations" element={<OperationsPage />} />
                        <Route path="/critical-nodes" element={<CriticalNodesPage />} />
                        <Route path="/map" element={<MapPage />} />
                        <Route path="/geofences" element={<GeofencesPage />} />
                        <Route path="/dispatch" element={<DispatchPage />} />
                        <Route path="/emergencies" element={<EmergenciesPage />} />
                        <Route path="/emergencies/:id" element={<EmergenciesPage />} />
                        <Route path="/credentials" element={<CredentialsPage />} />
                        <Route path="/patrol" element={<PatrolPage />} />
                        <Route path="/scheduling" element={<SchedulingPage />} />
                        <Route path="/safety" element={<SafetyPage />} />
                        <Route path="/evidence" element={<EvidencePage />} />
                        <Route path="/notifications" element={<NotificationsPage />} />
                        <Route path="/reports" element={<ReportsPage />} />
                        <Route path="/evaluation" element={<EvaluationPage />} />
                        <Route path="/manuals" element={<ManualsPage />} />
                        <Route path="/live" element={<LiveStreamPage />} />
                        <Route path="/signal" element={<SignalChatPage />} />

                        {/* Billing */}
                        <Route path="/billing" element={<BillingPage />} />

                        {/* Admin routes */}
                        <Route path="/admin/users" element={<UsersPage />} />
                        <Route path="/admin/agencies" element={<AgenciesPage />} />
                        <Route path="/admin/alerts" element={<AlertsPage />} />
                        <Route path="/admin/devices" element={<DeviceManagementPage />} />
                        <Route path="/admin/tools" element={<AdminToolsPage />} />
                        <Route path="/admin/enrollment" element={<EnrollmentPage />} />

                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    </Route>
                </Route>
            </Routes>

            {/* Gate de aceptación de documentos legales (post-login) */}
            <TermsGate />

            {/* Banner de consentimiento de cookies */}
            <CookieConsentBanner />

            {/* Toast notifications */}
            <Toaster position="top-right" richColors theme="dark" />
        </>
    )
}

export default App
